import { getHottestPredictions } from "@/services/getHottestPredictions";
import { ITake } from "@/types/ITake";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PredictionsState {
  predictions: ITake[];
  pageNumber: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  reset: boolean;
  searchString: string;
}

const initialState: PredictionsState = {
  predictions: [],
  pageNumber: 0,
  loading: false,
  error: null,
  hasMore: true,
  reset: false,
  searchString: "",
};

export const fetchTopPredictions = createAsyncThunk(
  "explore/predictionsSlice",
  async ({ pageNumber }: { pageNumber: number }, { rejectWithValue }) => {
    try {
      const data = await getHottestPredictions(pageNumber);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const PredictionsSlice = createSlice({
  name: "prediction_slice",
  initialState,
  reducers: {
    resetExplore: (state) => {
      state.pageNumber = 0;
      state.predictions = [];
      state.hasMore = true;
    },
    setReset: (state, action: PayloadAction<boolean>) => {
      state.reset = action.payload;
    },
    incrementPageNumberExplore: (state) => {
      state.pageNumber += 1;
    },
    updateScore: (
      state,
      action: PayloadAction<{ score: number; takeId: number }>
    ) => {
      const { score, takeId } = action.payload;

      return {
        ...state,
        predictions: state.predictions
          .map((take) => {
            if (take.id === takeId) {
              return {
                ...take,
                prediction: {
                  ...take.prediction,
                  score: Math.abs(score), // Set absolute score value
                },
              };
            }
            return take; // Return the unchanged take for others
          })
          .filter((take) => take.prediction.score !== 0)
          .sort((a, b) => b.prediction.score - a.prediction.score), // Sort by score, descending
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopPredictions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTopPredictions.fulfilled,
        (state, action: PayloadAction<ITake[]>) => {
          state.loading = false;
          const newHottestPredictions = action.payload;
          if (newHottestPredictions) {
            let newData = [...state.predictions, ...newHottestPredictions];
            // Filter out items with zero score
            newData = newData.filter((item) => item?.prediction?.score !== 0);
            const uniqueData = newData.filter(
              (v, i, a) => a.findIndex((t) => t.id === v.id) === i
            );
            state.predictions = uniqueData;
            if (newHottestPredictions.length === 0) {
              state.hasMore = false;
            }
          }
        }
      )
      .addCase(fetchTopPredictions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  incrementPageNumberExplore,
  resetExplore,
  setReset,
  updateScore,
} = PredictionsSlice.actions;

export default PredictionsSlice.reducer;
